import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from './Toast';
import api from '../../services/api';
import PolicyModal from './PolicyModal';

// One-time, mandatory data-sharing consent gate. Shown to STUDENTS only (the
// research/analytics data subjects) whose data_sharing_consent is explicitly
// false — i.e. Google OAuth student signups (who never saw the signup form) and
// any pre-existing student who never affirmatively consented. Counselors,
// parents, and admins manage the platform and are never gated. There is NO
// skip/dismiss: the box must be checked and submitted to proceed. New
// email/password signups already consent on the (now required) checkbox.
export default function ConsentGate() {
  const { user, updateUser } = useAuth();
  const { addToast } = useToast();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  // Only students are research/analytics data subjects — counselors, parents,
  // and admins manage the platform and are never gated. And only gate when we
  // explicitly know consent is false (unknown/true → no gate).
  if (!user || user.role !== 'student' || user.data_sharing_consent !== false) return null;

  const submit = async () => {
    if (!checked || saving) return;
    setSaving(true);
    try {
      await api.post('/auth/data-consent');
      updateUser({ data_sharing_consent: true });
    } catch (err) {
      addToast(err.response?.data?.error || 'Could not save your choice — please try again.', 'error');
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(3,5,12,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#0C1018', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 18, padding: 32, width: '100%', maxWidth: 460, boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
        <h2 style={{ fontSize: 22, fontWeight: 600, color: '#EAE6DC', margin: '0 0 8px' }}>Before you continue</h2>
        <p style={{ fontSize: 14, color: '#8A909C', lineHeight: 1.7, margin: '0 0 20px' }}>
          To use Inveira, we need your agreement on how your data may be used. This is a one-time step.
        </p>

        <label htmlFor="consentGate" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', background: 'rgba(76,130,247,0.06)', border: '1px solid rgba(76,130,247,0.14)', borderRadius: 10, padding: '14px 16px' }}>
          <input
            type="checkbox"
            id="consentGate"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            style={{ marginTop: 2, accentColor: '#4C82F7', width: 16, height: 16, flexShrink: 0 }}
          />
          <span style={{ fontSize: 13, color: '#C4C9D4', lineHeight: 1.6 }}>
            I agree to allow Inveira to use my anonymized data for research and analytics purposes.{' '}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setPolicyOpen(true); }}
              style={{ color: '#4C82F7', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline' }}
            >
              Learn more
            </button>
          </span>
        </label>

        <button
          type="button"
          onClick={submit}
          disabled={!checked || saving}
          style={{
            width: '100%', height: 46, marginTop: 20, borderRadius: 10, border: 'none',
            background: checked ? '#4C82F7' : 'rgba(76,130,247,0.35)',
            color: '#fff', fontSize: 14, fontWeight: 500,
            cursor: (!checked || saving) ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1, transition: 'background .15s, opacity .15s',
          }}
        >
          {saving ? 'Saving…' : 'Agree and continue'}
        </button>

        {policyOpen && <PolicyModal type="privacy" onClose={() => setPolicyOpen(false)} />}
      </div>
    </div>
  );
}
